import _, {sortBy} from 'lodash';
import React, {FC, useContext, useRef, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateLicenseTypesEnum} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {isTeammateNamespace} from '../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {PushContext} from '../../../../../libs/shared/core/src/services/push/pushContext';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {buildLensCounterKey} from '../../../../helpers/counterHelpers';
import {buildLensPath} from '../../../../helpers/modelHelpers';
import {buildAiReviewLensPath, isAiReviewLens} from '../../../../helpers/models/lensHelpers';
import {LensModel} from '../../../../models/api/lensModel';
import {
  CellsRefinementOptionsEnum,
  RefinementOptionModel,
} from '../../../../models/api/refinementOptionModel';
import {InboxesFiltersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {PermissionSimpleDropdownItem} from '../../../common/dropdowns/permissionSimpleDropdownItem';
import {WorkspaceDragSourceTypesEnum} from '../dnd/workspaceDragDropTypes';
import {LensUpdateHandler} from '../editor/context/workspacesEditorContext';
import {WorkspaceHeader} from '../header/workspaceHeader';
import {Workspace} from '../workspace';
import {LensesWorkspaceCreateDialog} from './dialog/lensesWorkspaceCreateDialog';
import {LensesConfirmHideDialog} from './lensesWorkspaceConfirmHide';
import {lensesWorkspaceSortComparator} from './lensesWorkspaceHelpers';
import {LensesWorkspaceItemContainer} from './lensesWorkspaceItemContainer';

/*
 * Props.
 */

export interface LensesWorkspaceProps {
  panelId: InboxesPanelId;
  /** Whether this workspace is collapsed or not. */
  isExpanded: boolean;
  /** Event handler to expand/collapse the workspace. */
  onToggleExpandClick: () => void;
  /** Current user license (full). */
  userLicenseType: TeammateLicenseTypesEnum;
  /** Whether this user is a global admin. */
  isUserGlobalAdmin: boolean;
  /** The list of lenses to display in this workspace. */
  lenses: ReadonlyArray<LensModel>;
  lensesSelectedRefinements: Map<LensModel['id'], RefinementOptionModel>;
  shouldAlphabetizeWorkspace: boolean;
  shouldUseOpenFilterForTeams: boolean;
  /** Event handler to hide the lenses workspace section. */
  onHideSection: () => void;
  /** Event handler to select lenses in P1. */
  onCreateLens: LensUpdateHandler;
  /** Whether this workspace is hidden */
  isHidden: boolean;
  isCreateDialogOpen: boolean;
  showCreateLensDialog: () => void;
  hideCreateLensDialog: () => void;
}

/*
 * Component.
 */

/** Displays the workspace of private and shared lenses. */
export const LensesWorkspace: FC<LensesWorkspaceProps> = (props) => {
  const {isConnected} = useContext(PushContext);
  const {
    panelId,
    isExpanded,
    userLicenseType,
    isHidden,
    isCreateDialogOpen,
    onHideSection,
    onCreateLens,
    showCreateLensDialog,
    hideCreateLensDialog,
    lenses,
  } = props;

  const workspaceRef = useRef<HTMLDivElement>(null);
  const [isHideInSidebarDialogOpen, setIsHideInSidebarDialogOpen] = useState<boolean>(false);

  const onHideSectionClick = () => {
    setIsHideInSidebarDialogOpen(true);
  };
  const closeHideSectionDialog = () => {
    setIsHideInSidebarDialogOpen(false);
  };

  // Hide the workspace completely for non-full teammates.
  if (userLicenseType !== TeammateLicenseTypesEnum.FULL) {
    return null;
  }

  const onToggleExpandClick = () => {
    props.onToggleExpandClick();
  };

  const isOffline = !isConnected;

  const createViewItem = (
    <PermissionSimpleDropdownItem onSelect={showCreateLensDialog}>
      <Trans id="lenses-workspace-create-lens">Create view</Trans>
    </PermissionSimpleDropdownItem>
  );
  return (
    <>
      {!isHidden && (
        <Workspace isExpanded={isExpanded} ref={workspaceRef} interactionId="lenses-workspace">
          {/* Header. */}
          <WorkspaceHeader
            panelId={panelId}
            interactionId="lenses"
            isExpanded={isExpanded}
            onToggleExpandClick={onToggleExpandClick}
            isOffline={isOffline}
            onHideSection={lenses.length === 0 ? onHideSectionClick : undefined}
            extraItems={createViewItem}
          >
            <Trans id="lenses-workspace-heading">Views</Trans>
          </WorkspaceHeader>

          {/* Workspace content. */}
          {maybeRenderExpandedContent(props, isOffline, showCreateLensDialog)}
        </Workspace>
      )}
      {isCreateDialogOpen && (
        <LensesWorkspaceCreateDialog
          panelId={props.panelId}
          onRequestClose={hideCreateLensDialog}
          workspaceUpdateHandler={onCreateLens}
        />
      )}
      {isHideInSidebarDialogOpen && (
        <LensesConfirmHideDialog onRequestClose={closeHideSectionDialog} onConfirmHide={onHideSection} />
      )}
    </>
  );
};

function trackLensClick(lens: LensModel) {
  trackInterfaceEvent(InterfaceEventsEnum.CLICK_LENS_P1, {lens_id: lens.id});
}

function maybeRenderExpandedContent(
  props: LensesWorkspaceProps,
  isOffline: boolean,
  onCreateClick: () => void,
) {
  const {
    panelId,
    isExpanded,
    shouldAlphabetizeWorkspace,
    lensesSelectedRefinements,
    shouldUseOpenFilterForTeams,
  } = props;

  if (!isExpanded) {
    return null;
  }

  const lastIndex = props.lenses.length - 1;

  const sortedLenses = shouldAlphabetizeWorkspace
    ? sortBy(props.lenses, lensesWorkspaceSortComparator)
    : props.lenses;

  return (
    <>
      {/* Lenses. */}
      {_(sortedLenses)
        .map((lens, index) => {
          const selectedRefinement = lensesSelectedRefinements.get(lens.id);

          const lensHasAssignees = lens.assigneeIds.length > 0;
          const refinementHasAssignees = selectedRefinement?.type === CellsRefinementOptionsEnum.ASSIGNEE;

          const filter = computeLensStatusFilter(
            lensHasAssignees,
            refinementHasAssignees,
            shouldUseOpenFilterForTeams,
          );

          const counterFilter = refinementHasAssignees ? InboxesFiltersEnum.OPEN : filter;

          return (
            <LensesWorkspaceItemContainer
              key={lens.id}
              panelId={panelId}
              lens={lens}
              to={
                isAiReviewLens(lens)
                  ? buildAiReviewLensPath(lens.id)
                  : buildLensPath(lens.id, filter, isTeammateNamespace(lens.namespace))
              }
              onClick={() => trackLensClick(lens)}
              counterKey={buildLensCounterKey(lens.id, counterFilter)}
              isDisabled={isOffline}
              workspaceDragItem={{
                type: WorkspaceDragSourceTypesEnum.LENS,
                id: lens.id,
                isLast: index === lastIndex,
              }}
              selectedRefinement={selectedRefinement}
              shouldAlphabetizeWorkspace={shouldAlphabetizeWorkspace}
            />
          );
        })
        .value()}
    </>
  );
}

/*
 * Helpers.
 */

export function computeLensStatusFilter(
  lensHasAssignees: boolean,
  refinementHasAssignees: boolean,
  shouldUseOpenFilterForTeams: boolean,
) {
  if (lensHasAssignees || shouldUseOpenFilterForTeams) {
    return InboxesFiltersEnum.OPEN;
  }

  if (refinementHasAssignees) {
    return InboxesFiltersEnum.ASSIGNED;
  }

  return InboxesFiltersEnum.UNASSIGNED;
}
